import { ComponentProps, useEffect, useMemo, useState } from "react";
import {
  GestureResponderEvent,
  Image,
  PanResponder,
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import styles from "./styles";
import { MediaVariant, Post, User } from "../../../../../types";
import { useDispatch, useSelector } from "react-redux";
import { throttle } from "throttle-debounce";
import { getLikeById, updateLike } from "../../../../services/posts";
import { AppDispatch, RootState } from "../../../../redux/store";
import { openCommentModal } from "../../../../redux/slices/modalSlice";
import {
  cycleLoopMode,
  enableBackgroundPlayback,
  LOOP_MODE_STORAGE_KEY,
  showScreenCover,
  toggleBackgroundPlayback,
  LoopMode,
} from "../../../../redux/slices/playbackSlice";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../../../../navigation/main";
import { Avatar } from "react-native-paper";
import { t } from "../../../../i18n/strings";
import { mediaVariantLabel } from "../../../../utils/mediaVariants";
import {
  fetchVideoActionState,
  setVideoFavorite,
} from "../../../../services/videoInteractions";
import VideoShareButton from "../../videoShareSheet";

type IoniconName = ComponentProps<typeof Ionicons>["name"];

const loopLabels: Record<LoopMode, string> = {
  single: t("loopSingle"),
  sequence: t("loopSequence"),
  shuffle: t("loopShuffle"),
  smart: t("loopSmart"),
};

const loopIcons: Record<LoopMode, IoniconName> = {
  single: "repeat",
  sequence: "reorder-three",
  shuffle: "shuffle",
  smart: "bulb",
};

/**
 * Function that renders a component meant to be overlapped on
 * top of the post with the post info like user's display name and avatar
 * and the post's description
 *
 * @param {User} user that created the post
 * @param {Post} post object
 */
export default function PostSingleOverlay({
  user,
  post,
  isPlaying,
  playbackProgress,
  progressVisible,
  onTogglePlayback,
  onSeek,
  strategyHeaderEnabled = false,
  progressTop,
  onFavoriteChange,
  onEnableBackgroundPlayback,
  backgroundLoadProgress,
  videoVariants,
  audioVariants,
  selectedVideoVariantId,
  selectedAudioVariantId,
  onSelectVideoVariant,
  onSelectAudioVariant,
}: {
  user: User;
  post: Post;
  isPlaying: boolean;
  playbackProgress: {
    positionMillis: number;
    durationMillis: number;
  };
  progressVisible: boolean;
  onTogglePlayback: () => void;
  onSeek: (progress: number) => void;
  strategyHeaderEnabled?: boolean;
  progressTop?: number;
  onFavoriteChange?: (favorite: boolean) => void;
  onEnableBackgroundPlayback?: () => void;
  backgroundLoadProgress?: number | null;
  videoVariants: MediaVariant[];
  audioVariants: MediaVariant[];
  selectedVideoVariantId: string | null;
  selectedAudioVariantId: string | null;
  onSelectVideoVariant: (variant: MediaVariant) => void;
  onSelectAudioVariant: (variant: MediaVariant | null) => void;
}) {
  const currentUser = useSelector((state: RootState) => state.auth.currentUser);
  const backgroundPlaybackEnabled = useSelector(
    (state: RootState) => state.playback.backgroundPlaybackEnabled,
  );
  const loopMode = useSelector((state: RootState) => state.playback.loopMode);
  const dispatch: AppDispatch = useDispatch();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();

  const [currentLikeState, setCurrentLikeState] = useState({
    state: false,
    counter: post.likesCount,
  });
  const [currentCommentsCount, setCurrentCommentsCount] = useState(
    post.commentsCount,
  );
  const [progressWidth, setProgressWidth] = useState(0);
  const [variantMenu, setVariantMenu] = useState<"video" | "audio" | null>(
    null,
  );
  const [favorited, setFavorited] = useState(Boolean(post.favorited));
  const [favoriteSaving, setFavoriteSaving] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(LOOP_MODE_STORAGE_KEY, loopMode);
    } catch {}
  }, [loopMode]);

  useEffect(() => {
    if (currentUser) {
      getLikeById(post.id, currentUser.uid).then((res) => {
        setCurrentLikeState({
          ...currentLikeState,
          state: res,
        });
      });
    }
  }, []);

  useEffect(() => {
    setFavorited(Boolean(post.favorited));
    if (!post.videoServerId) return;
    let active = true;
    fetchVideoActionState(post.videoServerId, currentUser)
      .then((state) => {
        if (active) setFavorited(state.favorited);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [currentUser?.uid, post.favorited, post.videoServerId]);

  const handleFavorite = async () => {
    if (!post.videoServerId || favoriteSaving) return;
    const nextFavorite = !favorited;
    setFavorited(nextFavorite);
    setFavoriteSaving(true);
    try {
      await setVideoFavorite(
        post.videoServerId,
        post.description,
        currentUser,
        nextFavorite,
      );
      onFavoriteChange?.(nextFavorite);
    } catch {
      setFavorited(!nextFavorite);
    } finally {
      setFavoriteSaving(false);
    }
  };

  /**
   * Handles the like button action.
   *
   * In order to make the action more snappy the like action
   * is optimistic, meaning we don't wait for a response from the
   * server and always assume the write/delete action is successful
   */
  const handleUpdateLike = useMemo(
    () =>
      throttle(500, (currentLikeStateInst: typeof currentLikeState) => {
        setCurrentLikeState({
          state: !currentLikeStateInst.state,
          counter:
            currentLikeStateInst.counter +
            (currentLikeStateInst.state ? -1 : 1),
        });
        if (currentUser) {
          updateLike(post.id, currentUser.uid, currentLikeStateInst.state);
        }
      }),
    [],
  );

  const handleUpdateCommentCount = () => {
    setCurrentCommentsCount((prevCount) => prevCount + 1);
  };

  const handleBackgroundPlaybackPress = () => {
    if (backgroundPlaybackEnabled) {
      dispatch(toggleBackgroundPlayback());
      return;
    }

    dispatch(enableBackgroundPlayback());
    dispatch(showScreenCover());
    onEnableBackgroundPlayback?.();
  };

  const progressPercent =
    playbackProgress.durationMillis > 0
      ? Math.min(
          100,
          Math.max(
            0,
            (playbackProgress.positionMillis /
              playbackProgress.durationMillis) *
              100,
          ),
        )
      : 0;
  const seekFromEvent = (event: GestureResponderEvent) => {
    if (progressWidth <= 0) {
      return;
    }
    onSeek(event.nativeEvent.locationX / progressWidth);
  };
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        onPanResponderGrant: seekFromEvent,
        onPanResponderMove: seekFromEvent,
      }),
    [onSeek, progressWidth],
  );

  return (
    <View pointerEvents="box-none" style={styles.container}>
      {!backgroundPlaybackEnabled && (
        <View style={styles.infoContainer}>
          <Text style={styles.displayName}>
            {user.displayName || user.email}
          </Text>
          <Text style={styles.description}>{post.description}</Text>
        </View>
      )}
      <View style={styles.leftContainer}>
        {!backgroundPlaybackEnabled && (
          <>
            <TouchableOpacity
              onPress={() =>
                navigation.navigate("profileOther", {
                  initialUserId: user?.uid ?? "",
                })
              }
            >
              {user.photoURL ? (
                <Image style={styles.avatar} source={{ uri: user.photoURL }} />
              ) : (
                <Avatar.Icon
                  style={styles.defaultAvatar}
                  size={50}
                  icon={"account"}
                />
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => handleUpdateLike(currentLikeState)}
            >
              <Ionicons
                color="white"
                size={40}
                name={currentLikeState.state ? "heart" : "heart-outline"}
              />
              <Text style={styles.actionButtonText}>
                {currentLikeState.counter}
              </Text>
            </TouchableOpacity>
            {post.videoServerId && (
              <TouchableOpacity
                accessibilityRole="button"
                accessibilityLabel={favorited ? "取消收藏" : "收藏视频"}
                disabled={favoriteSaving}
                style={styles.actionButton}
                onPress={handleFavorite}
              >
                <Ionicons
                  color={favorited ? "#e2bd77" : "white"}
                  size={34}
                  name={favorited ? "bookmark" : "bookmark-outline"}
                />
                <Text
                  style={[
                    styles.actionButtonText,
                    favorited && styles.actionButtonTextActive,
                  ]}
                >
                  {favorited ? "已收藏" : "收藏"}
                </Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.actionButton}
              onPress={() =>
                dispatch(
                  openCommentModal({
                    open: true,
                    data: post,
                    modalType: 0,
                    onCommentSend: handleUpdateCommentCount,
                  }),
                )
              }
            >
              <Ionicons color="white" size={40} name={"chatbubble"} />
              <Text style={styles.actionButtonText}>
                {currentCommentsCount}
              </Text>
            </TouchableOpacity>
            {post.videoServerId ? (
              <VideoShareButton
                variant="feed"
                video={{
                  id: post.videoServerId,
                  title: post.description,
                  thumbnail: post.media[1],
                }}
              />
            ) : null}
            <TouchableOpacity
              style={styles.actionButton}
              onPress={onTogglePlayback}
            >
              <Ionicons
                color="white"
                size={34}
                name={isPlaying ? "pause-circle" : "play-circle"}
              />
              <Text style={styles.actionButtonText}>
                {isPlaying ? t("playbackPause") : t("playbackResume")}
              </Text>
            </TouchableOpacity>
          </>
        )}
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleBackgroundPlaybackPress}
        >
          <Ionicons
            color={backgroundPlaybackEnabled ? "#28c99a" : "white"}
            size={32}
            name={backgroundPlaybackEnabled ? "moon" : "moon-outline"}
          />
          <Text
            style={[
              styles.actionButtonText,
              backgroundPlaybackEnabled && styles.actionButtonTextActive,
            ]}
          >
            {t("playbackBackground")}
          </Text>
        </TouchableOpacity>
        {backgroundPlaybackEnabled && backgroundLoadProgress !== null && (
          <View style={styles.backgroundLoadIndicator}>
            <View style={styles.backgroundLoadCircle}>
              <Text style={styles.backgroundLoadPercent}>
                {Math.round(backgroundLoadProgress ?? 0)}%
              </Text>
            </View>
            <Text style={styles.backgroundLoadLabel}>M4A 音频</Text>
          </View>
        )}
        {!backgroundPlaybackEnabled && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() =>
              setVariantMenu((current) =>
                current === "video" ? null : "video",
              )
            }
          >
            <Ionicons color="white" size={31} name="settings-outline" />
            <Text style={styles.actionButtonText}>画质</Text>
          </TouchableOpacity>
        )}
        {!backgroundPlaybackEnabled && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() =>
              setVariantMenu((current) =>
                current === "audio" ? null : "audio",
              )
            }
          >
            <Ionicons color="white" size={31} name="volume-high-outline" />
            <Text style={styles.actionButtonText}>音质</Text>
          </TouchableOpacity>
        )}
        {!backgroundPlaybackEnabled && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => dispatch(cycleLoopMode())}
          >
            <Ionicons color="#28c99a" size={32} name={loopIcons[loopMode]} />
            <Text
              style={[styles.actionButtonText, styles.actionButtonTextActive]}
            >
              {loopLabels[loopMode]}
            </Text>
          </TouchableOpacity>
        )}
      </View>
      {variantMenu && (
        <View style={styles.variantPanel}>
          <View style={styles.variantPanelHeader}>
            <Text style={styles.variantPanelTitle}>
              {variantMenu === "video" ? "选择画质" : "选择音质"}
            </Text>
            <TouchableOpacity onPress={() => setVariantMenu(null)}>
              <Ionicons color="white" size={22} name="close" />
            </TouchableOpacity>
          </View>
          <ScrollView style={styles.variantOptions}>
            {variantMenu === "audio" && (
              <TouchableOpacity
                style={[
                  styles.variantOption,
                  selectedAudioVariantId === null && styles.variantOptionActive,
                ]}
                onPress={() => {
                  onSelectAudioVariant(null);
                  setVariantMenu(null);
                }}
              >
                <Text style={styles.variantOptionText}>自动（跟随视频）</Text>
                {selectedAudioVariantId === null && (
                  <Ionicons color="#28c99a" size={18} name="checkmark" />
                )}
              </TouchableOpacity>
            )}
            {(variantMenu === "video" ? videoVariants : audioVariants).map(
              (variant) => {
                const selected =
                  variantMenu === "video"
                    ? variant.id === selectedVideoVariantId
                    : variant.id === selectedAudioVariantId;
                return (
                  <TouchableOpacity
                    key={variant.id}
                    style={[
                      styles.variantOption,
                      selected && styles.variantOptionActive,
                    ]}
                    onPress={() => {
                      if (variantMenu === "video") {
                        onSelectVideoVariant(variant);
                      } else {
                        onSelectAudioVariant(variant);
                      }
                      setVariantMenu(null);
                    }}
                  >
                    <View style={styles.variantOptionCopy}>
                      <Text style={styles.variantOptionText}>
                        {mediaVariantLabel(variant)}
                      </Text>
                      <Text style={styles.variantOptionMeta}>
                        {variant.resolution || variant.bitrate}
                        {variant.resolution && variant.bitrate
                          ? ` · ${variant.bitrate}`
                          : ""}
                      </Text>
                    </View>
                    {selected && (
                      <Ionicons color="#28c99a" size={18} name="checkmark" />
                    )}
                  </TouchableOpacity>
                );
              },
            )}
            {(variantMenu === "video" ? videoVariants : audioVariants)
              .length === 0 && (
              <Text style={styles.variantEmpty}>此视频没有其他版本</Text>
            )}
          </ScrollView>
        </View>
      )}
      {progressVisible && playbackProgress.durationMillis > 0 && (
        <View
          style={[
            styles.progressHitArea,
            typeof progressTop === "number" && { top: progressTop },
          ]}
          onLayout={(event) => setProgressWidth(event.nativeEvent.layout.width)}
          {...panResponder.panHandlers}
        >
          <View style={styles.progressTrack}>
            <View
              style={[styles.progressFill, { width: `${progressPercent}%` }]}
            />
            <View
              style={[styles.progressThumb, { left: `${progressPercent}%` }]}
            />
          </View>
        </View>
      )}
    </View>
  );
}
